/**
 * PayrollPage.js — Owner-only Attendance & Payroll page (2026-06-10).
 *
 * Hard role gate at the top: only `owner` / `super_admin` may load this
 * page. The backend mirrors the gate on every endpoint — but we also
 * fail-fast here to avoid leaking even the layout to Admin/Manager/Team.
 *
 * Layout:
 *   • Workspace overview table — one row per employee with payroll
 *     profile + period totals + payable amount.
 *   • Click a row → opens the per-employee drawer with:
 *       - Payroll profile editor (country, salary, sick allowance, ...)
 *       - Full attendance sheet (per-day status table)
 *       - Summary card (required vs worked vs shortfall vs payable)
 *       - Export CSV button.
 *
 * Theme-aware (no hardcoded hex). All currency is rendered as raw
 * numbers — Owner sees the workspace's preferred symbol via the helper.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { payrollAPI, workspaceAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Wallet, Download, RefreshCw, AlertTriangle, ChevronRight, ShieldAlert, Save, Calendar } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { toast } from 'sonner';
import { StellarDatePicker } from '../components/StellarFormControls';
import { AutoGrowTextarea } from '../components/AutoGrowTextarea';

const STATUS_TONE = {
  present:     'text-emerald-500 border-emerald-500/30 bg-emerald-500/10',
  partial_day: 'text-amber-500 border-amber-500/30 bg-amber-500/10',
  absent:      'text-red-500 border-red-500/30 bg-red-500/10',
  leave:       'text-sky-500 border-sky-500/30 bg-sky-500/10',
  sick_leave:  'text-purple-500 border-purple-500/30 bg-purple-500/10',
  holiday:     'text-gold border-gold/40 bg-gold/10',
  makeup_day:  'text-cyan-500 border-cyan-500/30 bg-cyan-500/10',
  off:         'text-muted-foreground border-border/30 bg-muted/40',
};

const STATUS_LABEL = {
  present: 'Present', partial_day: 'Partial', absent: 'Absent',
  leave: 'Leave', sick_leave: 'Sick Leave', holiday: 'Holiday',
  makeup_day: 'Makeup', off: 'Off',
};

function currentMonthRange() {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
}

function fmtMoney(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PayrollPage() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const isOwner = user?.role === 'super_admin' || user?.role === 'owner';

  const { start: defStart, end: defEnd } = currentMonthRange();
  const [periodStart, setPeriodStart] = useState(defStart);
  const [periodEnd, setPeriodEnd] = useState(defEnd);
  const [overview, setOverview] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchAll = async () => {
    if (!wsId || !isOwner) return;
    setLoading(true);
    try {
      const [ov, mem] = await Promise.all([
        payrollAPI.overview(wsId, periodStart, periodEnd),
        workspaceAPI.listAssignableMembers(wsId).catch(() => ({ data: [] })),
      ]);
      setOverview(ov.data || []);
      const mapped = (mem.data || [])
        .map((m) => ({
          id: m.user_id,
          name: m.user?.name || m.user?.email || '?',
          email: m.user?.email || '',
          role: m.role || '',
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setMembers(mapped);
    } catch (err) {
      console.error('[payroll] overview load failed', err);
      toast.error('Failed to load payroll overview');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [wsId, periodStart, periodEnd, isOwner]);

  const totals = useMemo(() => {
    const t = overview.reduce((acc, r) => ({
      base: acc.base + (r.base_salary || 0),
      deduction: acc.deduction + (r.deduction_amount || 0),
      payable: acc.payable + (r.payable_amount || 0),
      shortfall: acc.shortfall + (r.unpaid_shortfall_hours || 0),
    }), { base: 0, deduction: 0, payable: 0, shortfall: 0 });
    return t;
  }, [overview]);

  // Hard role gate — page should never render anything sensitive to non-owners.
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8" data-testid="payroll-no-access">
        <Card>
          <CardContent className="p-10 text-center space-y-3">
            <ShieldAlert className="h-10 w-10 mx-auto text-amber-500" />
            <h2 className="text-lg font-display">Owner only</h2>
            <p className="text-[13px] text-muted-foreground">
              Attendance & Payroll is restricted to the Owner. Please contact your workspace owner.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Build the list of profile-less members so Owner can add them.
  const profileUserIds = new Set(overview.map(o => o.user_id));
  const withoutProfile = members.filter(m => !profileUserIds.has(m.id));

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-5" data-testid="payroll-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-gold" /> Attendance & Payroll
          </h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            Owner-only. Daily clock-ins, leaves, and holidays are rolled up here per employee.
            Math is hour-based — partial work days, makeup hours, and overflow sick leave are tracked correctly.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="payroll-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Refresh
        </Button>
      </div>

      {/* Period filter */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="payroll-period">
        <div className="flex items-center gap-2">
          <Label className="text-[12px]">From</Label>
          <StellarDatePicker value={periodStart} onChange={setPeriodStart} testId="payroll-start" clearable={false} />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[12px]">To</Label>
          <StellarDatePicker value={periodEnd} onChange={setPeriodEnd} testId="payroll-end" minDate={periodStart || undefined} clearable={false} />
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => { const r = currentMonthRange(); setPeriodStart(r.start); setPeriodEnd(r.end); }} data-testid="payroll-preset-month">This month</Button>
          <Button size="sm" variant="ghost" onClick={() => {
            const d = new Date(); d.setMonth(d.getMonth() - 1);
            const s = new Date(d.getFullYear(), d.getMonth(), 1);
            const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
            const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
            setPeriodStart(fmt(s)); setPeriodEnd(fmt(e));
          }} data-testid="payroll-preset-last">Last month</Button>
        </div>
      </div>

      {/* Totals tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Employees', value: overview.length, testid: 'payroll-tile-count' },
          { label: 'Total base', value: fmtMoney(totals.base), testid: 'payroll-tile-base' },
          { label: 'Total deductions', value: fmtMoney(totals.deduction), testid: 'payroll-tile-deduction' },
          { label: 'Total payable', value: fmtMoney(totals.payable), testid: 'payroll-tile-payable' },
        ].map((t) => (
          <Card key={t.label}>
            <CardContent className="pt-5">
              <div className="text-[11px] uppercase text-muted-foreground">{t.label}</div>
              <div className="text-2xl font-semibold mt-1 text-foreground" data-testid={t.testid}>{t.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overview table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left border-b border-border/30 text-[11px] uppercase text-muted-foreground">
                <th className="px-4 py-2">Employee</th>
                <th className="px-4 py-2">Country</th>
                <th className="px-4 py-2 text-right">Required</th>
                <th className="px-4 py-2 text-right">Worked</th>
                <th className="px-4 py-2 text-right">Paid Leave</th>
                <th className="px-4 py-2 text-right">Sick</th>
                <th className="px-4 py-2 text-right">Holiday</th>
                <th className="px-4 py-2 text-right">Shortfall</th>
                <th className="px-4 py-2 text-right">Base</th>
                <th className="px-4 py-2 text-right">Deduction</th>
                <th className="px-4 py-2 text-right">Payable</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && overview.length === 0 ? (
                <tr><td colSpan={12} className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                </td></tr>
              ) : overview.length === 0 ? (
                <tr><td colSpan={12} className="p-8 text-center text-muted-foreground" data-testid="payroll-empty">
                  No payroll profiles yet. Add an employee below to start.
                </td></tr>
              ) : overview.map((r) => (
                <tr
                  key={r.user_id}
                  className="border-b border-border/20 hover:bg-foreground/[0.03] cursor-pointer"
                  onClick={() => setSelectedUser({ id: r.user_id, name: r.user?.name, email: r.user?.email })}
                  data-testid={`payroll-row-${r.user_id}`}
                >
                  <td className="px-4 py-2">
                    <div className="font-medium text-foreground">{r.user?.name}</div>
                    <div className="text-[11px] text-muted-foreground">{r.user?.email}</div>
                  </td>
                  <td className="px-4 py-2"><Badge variant="outline" className="text-[10px]">{r.country}</Badge></td>
                  <td className="px-4 py-2 text-right">{r.required_hours}h</td>
                  <td className="px-4 py-2 text-right">{r.worked_hours}h</td>
                  <td className="px-4 py-2 text-right">{r.paid_leave_hours}h</td>
                  <td className="px-4 py-2 text-right">{r.sick_leave_hours_used}h</td>
                  <td className="px-4 py-2 text-right">{r.holiday_credit_hours}h</td>
                  <td className={`px-4 py-2 text-right ${r.unpaid_shortfall_hours > 0 ? 'text-red-500' : ''}`}>{r.unpaid_shortfall_hours}h</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(r.base_salary)}</td>
                  <td className={`px-4 py-2 text-right ${r.deduction_amount > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>{fmtMoney(r.deduction_amount)}</td>
                  <td className="px-4 py-2 text-right font-semibold text-gold">{fmtMoney(r.payable_amount)}</td>
                  <td className="px-4 py-2 text-right"><ChevronRight className="h-4 w-4 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add employee → payroll profile */}
      {withoutProfile.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Add employee to payroll</p>
            <div className="flex flex-wrap gap-2">
              {withoutProfile.map(m => (
                <button
                  key={m.id}
                  onClick={() => setSelectedUser({ id: m.id, name: m.name, email: m.email, isNew: true })}
                  className="text-[12px] px-3 py-1.5 rounded-full border border-border/40 hover:border-gold/50 hover:bg-gold/5 transition-colors"
                  data-testid={`payroll-add-employee-${m.id}`}
                >
                  + {m.name} <span className="text-muted-foreground/50">({m.role})</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <PayrollEmployeeDrawer
        wsId={wsId}
        target={selectedUser}
        periodStart={periodStart}
        periodEnd={periodEnd}
        onClose={(refresh) => {
          setSelectedUser(null);
          if (refresh) fetchAll();
        }}
      />
    </div>
  );
}

// ── Per-employee drawer ───────────────────────────────────────────────
function PayrollEmployeeDrawer({ wsId, target, periodStart, periodEnd, onClose }) {
  const open = !!target;
  const [sheet, setSheet] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    country: 'IN',
    monthly_salary: 0,
    biweekly_salary: 0,
    pay_cycle: 'monthly',
    standard_hours_per_day: 8,
    paid_break_minutes: 30,
    monthly_sick_leave_hours: 16,
    paid_national_holidays: true,
    working_days_per_week: [0, 1, 2, 3, 4],
    notes: '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !wsId || !target?.id) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setSheet(null);
      setProfile(null);
      // Always fetch the profile (404 → still allow creation)
      try {
        const r = await payrollAPI.getProfile(wsId, target.id);
        if (cancelled) return;
        const p = r.data && Object.keys(r.data).length ? r.data : null;
        setProfile(p);
        if (p) {
          setProfileForm({
            country: p.country || 'IN',
            monthly_salary: p.monthly_salary || 0,
            biweekly_salary: p.biweekly_salary || 0,
            pay_cycle: p.pay_cycle || 'monthly',
            standard_hours_per_day: p.standard_hours_per_day ?? 8,
            paid_break_minutes: p.paid_break_minutes ?? 30,
            monthly_sick_leave_hours: p.monthly_sick_leave_hours ?? 16,
            paid_national_holidays: !!p.paid_national_holidays,
            working_days_per_week: p.working_days_per_week || [0, 1, 2, 3, 4],
            notes: p.notes || '',
          });
        } else {
          setProfileForm((f) => ({ ...f, country: 'IN', monthly_sick_leave_hours: 16, paid_national_holidays: true }));
        }
      } catch (_) {
        if (!cancelled) setProfile(null);
      }
      // Sheet — only useful if profile exists
      try {
        const r = await payrollAPI.sheet(wsId, target.id, periodStart, periodEnd);
        if (!cancelled) setSheet(r.data);
      } catch (err) {
        if (!cancelled) setSheet(null);
        if (err?.response?.status !== 404) console.error('[sheet]', err);
      }
      if (!cancelled) setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [open, wsId, target?.id, periodStart, periodEnd]);

  // Auto-flip sensible defaults when country toggles — applied directly
  // on change to avoid useEffect-state-write lint hits.
  const applyCountryDefaults = (next) => {
    setProfileForm((f) => {
      const out = { ...f, country: next };
      if (next === 'CA') {
        return { ...out, pay_cycle: 'biweekly', monthly_sick_leave_hours: 0, paid_national_holidays: false, paid_break_minutes: 0 };
      }
      return { ...out, pay_cycle: 'monthly', monthly_sick_leave_hours: 16, paid_national_holidays: true, paid_break_minutes: 30 };
    });
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await payrollAPI.upsertProfile(wsId, target.id, profileForm);
      toast.success('Payroll profile saved');
      // Reload sheet after save
      const r = await payrollAPI.sheet(wsId, target.id, periodStart, periodEnd);
      setSheet(r.data);
      setProfile(profileForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(true); }}>
      <SheetContent side="right" className="sm:max-w-[720px] overflow-y-auto bg-card" data-testid="payroll-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-[18px]">{target?.name}</SheetTitle>
          <SheetDescription className="text-[12px] text-muted-foreground">
            {target?.email} • Period {periodStart} → {periodEnd}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-6">
          {/* Payroll profile editor */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Payroll profile</p>
                <Badge variant="outline" className="text-[10px]">{profile ? 'Active' : 'Not configured'}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px]">Country</Label>
                  <select
                    value={profileForm.country}
                    onChange={(e) => applyCountryDefaults(e.target.value)}
                    className="w-full h-9 rounded-md bg-background border border-border/40 px-3 text-[13px]"
                    data-testid="payroll-form-country"
                  >
                    <option value="IN">India (IN)</option>
                    <option value="CA">Canada (CA)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[12px]">Pay cycle</Label>
                  <select
                    value={profileForm.pay_cycle}
                    onChange={(e) => setProfileForm((f) => ({ ...f, pay_cycle: e.target.value }))}
                    className="w-full h-9 rounded-md bg-background border border-border/40 px-3 text-[13px]"
                    data-testid="payroll-form-paycycle"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="biweekly">Bi-weekly</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[12px]">{profileForm.pay_cycle === 'monthly' ? 'Monthly salary' : 'Bi-weekly salary'}</Label>
                  <Input
                    type="number"
                    value={profileForm.pay_cycle === 'monthly' ? profileForm.monthly_salary : profileForm.biweekly_salary}
                    onChange={(e) => setProfileForm((f) => ({
                      ...f,
                      [profileForm.pay_cycle === 'monthly' ? 'monthly_salary' : 'biweekly_salary']: Number(e.target.value) || 0,
                    }))}
                    className="h-9 text-[13px]"
                    data-testid="payroll-form-salary"
                  />
                </div>
                <div>
                  <Label className="text-[12px]">Standard hours / day</Label>
                  <Input
                    type="number" step="0.5"
                    value={profileForm.standard_hours_per_day}
                    onChange={(e) => setProfileForm((f) => ({ ...f, standard_hours_per_day: Number(e.target.value) || 0 }))}
                    className="h-9 text-[13px]"
                    data-testid="payroll-form-stdhours"
                  />
                </div>
                <div>
                  <Label className="text-[12px]">Paid break (minutes)</Label>
                  <Input
                    type="number"
                    value={profileForm.paid_break_minutes}
                    onChange={(e) => setProfileForm((f) => ({ ...f, paid_break_minutes: Number(e.target.value) || 0 }))}
                    className="h-9 text-[13px]"
                    data-testid="payroll-form-paidbreak"
                  />
                </div>
                <div>
                  <Label className="text-[12px]">Paid sick / month (hours)</Label>
                  <Input
                    type="number" step="0.5"
                    value={profileForm.monthly_sick_leave_hours}
                    onChange={(e) => setProfileForm((f) => ({ ...f, monthly_sick_leave_hours: Number(e.target.value) || 0 }))}
                    className="h-9 text-[13px]"
                    data-testid="payroll-form-sickhours"
                  />
                </div>
                <div className="col-span-2 flex items-center gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={profileForm.paid_national_holidays}
                    onChange={(e) => setProfileForm((f) => ({ ...f, paid_national_holidays: e.target.checked }))}
                    data-testid="payroll-form-paidholidays"
                  />
                  <span>National holidays are paid</span>
                </div>
                <div className="col-span-2">
                  <Label className="text-[12px]">Notes (private)</Label>
                  <AutoGrowTextarea
                    value={profileForm.notes}
                    onChange={(e) => setProfileForm((f) => ({ ...f, notes: e.target.value }))}
                    minRows={6}
                    data-testid="payroll-form-notes"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button size="sm" onClick={saveProfile} disabled={saving} className="bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30" data-testid="payroll-form-save">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Save profile
                </Button>
                {profile && (
                  <a
                    href={payrollAPI.exportSheetUrl(wsId, target.id, periodStart, periodEnd) + `&t=${encodeURIComponent(localStorage.getItem('stellar_token') || '')}`}
                    onClick={async (e) => {
                      // The auth header isn't sent for plain anchor downloads. We do a fetch + blob so the JWT goes through.
                      e.preventDefault();
                      try {
                        const r = await fetch(payrollAPI.exportSheetUrl(wsId, target.id, periodStart, periodEnd), {
                          headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
                        });
                        if (!r.ok) throw new Error('export failed');
                        const blob = await r.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `payroll_${(target?.name || target?.id || 'user').replace(/\s+/g, '_')}_${periodStart}_${periodEnd}.csv`;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) { toast.error('Export failed'); }
                    }}
                    className="text-[13px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/40 hover:border-gold/40 transition-colors"
                    data-testid="payroll-export-csv"
                  >
                    <Download className="h-3.5 w-3.5" /> Export CSV
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Summary card */}
          {sheet && (
            <Card>
              <CardContent className="p-4 space-y-3" data-testid="payroll-summary">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Summary</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]">
                  <SummaryRow label="Required hours" value={`${sheet.required_hours}h`} />
                  <SummaryRow label="Worked hours" value={`${sheet.worked_hours}h`} />
                  <SummaryRow label="Paid leave used" value={`${sheet.paid_leave_hours}h`} />
                  <SummaryRow label="Sick leave used" value={`${sheet.sick_leave_hours_used}h / ${sheet.sick_leave_hours_used + sheet.sick_leave_hours_remaining}h`} />
                  <SummaryRow label="Holiday credits" value={`${sheet.holiday_credit_hours}h`} />
                  <SummaryRow label="Credited total" value={`${sheet.credited_hours}h`} />
                  <SummaryRow label="Overtime (not paid)" value={`${sheet.overtime_hours}h`} tone="muted" />
                  <SummaryRow label="Unpaid shortfall" value={`${sheet.unpaid_shortfall_hours}h`} tone={sheet.unpaid_shortfall_hours > 0 ? 'warn' : 'ok'} />
                  <SummaryRow label="Hourly rate" value={fmtMoney(sheet.hourly_rate)} />
                  <SummaryRow label="Base salary" value={fmtMoney(sheet.base_salary)} />
                  <SummaryRow label="Deduction" value={fmtMoney(sheet.deduction_amount)} tone={sheet.deduction_amount > 0 ? 'warn' : 'muted'} />
                  <div className="col-span-2 border-t border-border/30 pt-2 mt-1 flex items-center justify-between">
                    <span className="text-[12px] uppercase text-muted-foreground">Final payable</span>
                    <span className="text-xl font-display font-semibold text-gold" data-testid="payroll-payable-amount">{fmtMoney(sheet.payable_amount)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily attendance table */}
          {sheet && sheet.daily && sheet.daily.length > 0 && (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="border-b border-border/30 text-[10px] uppercase text-muted-foreground">
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Clock In</th>
                      <th className="px-3 py-2 text-left">Clock Out</th>
                      <th className="px-3 py-2 text-right">Worked</th>
                      <th className="px-3 py-2 text-right">Break</th>
                      <th className="px-3 py-2 text-right">Credited</th>
                      <th className="px-3 py-2 text-right">Shortfall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.daily.map((r) => (
                      <tr key={r.date} className="border-b border-border/15">
                        <td className="px-3 py-1.5">{r.date} <span className="text-muted-foreground/60">({r.weekday})</span></td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] border ${STATUS_TONE[r.status] || STATUS_TONE.off}`}>
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.clock_in ? formatTime(r.clock_in) : '—'}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.clock_out ? formatTime(r.clock_out) : '—'}</td>
                        <td className="px-3 py-1.5 text-right">{r.worked_hours}h</td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">{r.break_hours}h</td>
                        <td className="px-3 py-1.5 text-right">{r.credited_hours}h</td>
                        <td className={`px-3 py-1.5 text-right ${r.unpaid_shortfall_hours > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>{r.unpaid_shortfall_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {loading && (
            <div className="text-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading sheet…
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SummaryRow({ label, value, tone }) {
  const toneCls = tone === 'warn' ? 'text-red-500' : tone === 'ok' ? 'text-emerald-500' : tone === 'muted' ? 'text-muted-foreground' : 'text-foreground';
  return (
    <>
      <div className="text-muted-foreground">{label}</div>
      <div className={`text-right ${toneCls}`}>{value}</div>
    </>
  );
}

function formatTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
}
